import React, {useState, useEffect} from 'react';
import ProjectsHero from '../../Components/Projects/ProjectsHero';
import SwitchMUI from '../../../shared/SwitchMUI';
import {ReactComponent as MuteSvg} from '../../../assets/icons/mute.svg';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import './ProjectsNotification.scss';
import {getProjectNotifications, patchProjectNotifications} from '../../../redux/actions/projects/projectsActions';
import {notifySuccess} from '../../../helpers/notifySnack';
import LoadPanel from '../../Components/LoadPanel';
import ButtonMUI from '../../../shared/ButtonMUI';
import IconButtonMUI from '../../../shared/IconButtonMUI';
import {hasPermission} from "../../../helpers/functions";
import {PERMISSIONS} from "../../../helpers/constants";

const ProjectsNotification = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const [buttonLoad, setButtonLoad] = useState(false);

    const [settings, setSettings] = useState({
        photoFeedback: true,
        videoFeedback: true,
        blueprint: true,
        documents: true,
        estimate: true,
        messages: true,
        membersEdited: true
    });

    const [initialSettings, setInitialSettings] = useState(settings);

    useEffect(() => {
        dispatch(getProjectNotifications(singleProject.id));
    }, [dispatch]);

    const {singleProject, projectNotifications, projectNotificationsLoading} = useSelector((state) => state.projects);

    const {profileMe} = useSelector((state) => state.profile);
    const allowActions = singleProject?.status === 'active' || singleProject?.status === 'pause'

    const permissionEstimate = hasPermission(PERMISSIONS.ESTIMATE_LIST, profileMe?.permissions ?? []);
    const notification = [
        {
            title: t('profileNotification.notifications.photoReportTitle'),
            descriptions: t('profileNotification.notifications.photoReport'),
            key: 'photoFeedback'
        },
        {
            title: t('profileNotification.notifications.videoReportTitle'),
            descriptions: t('profileNotification.notifications.videoReport'),
            key: 'videoFeedback'
        },
        {
            title: t('profileNotification.notifications.drawingsTitle'),
            descriptions: t('profileNotification.notifications.drawings'),
            key: 'blueprint'
        },
        {
            title: t('profileNotification.notifications.documentsTitle'),
            descriptions: t('profileNotification.notifications.documents'),
            key: 'documents'
        },
        ...(permissionEstimate
            ? [
                {
                    title: t('profileNotification.notifications.estimateTitle'),
                    descriptions: t('profileNotification.notifications.estimate'),
                    key: 'estimate'
                }
            ]
            : []),
        {
            title: t('profileNotification.notifications.participantChangesTitle'),
            descriptions: t('profileNotification.notifications.participantChanges'),
            key: 'membersEdited'
        },
        {
            title: t('profileNotification.notifications.chat'),
            descriptions: t('profileNotification.notifications.chatMessages'),
            key: 'messages'
        }
    ];

    useEffect(() => {
        if (projectNotifications && Object.keys(projectNotifications).length) {
            setSettings(projectNotifications);
            setInitialSettings(projectNotifications);
        }
    }, [projectNotifications]);

    const handleSwitchChange = (key) => (event) => {
        setSettings({
            ...settings,
            [key]: event.target.checked
        });
    };

    const handleNotificationRead = async () => {
        const updatedSettings = {
            photoFeedback: false,
            videoFeedback: false,
            blueprint: false,
            documents: false,
            estimate: false,
            membersEdited: false
        };

        try {
            const response = await dispatch(patchProjectNotifications(updatedSettings, singleProject.id));
            if (response.payload?.status === 200) {
                setSettings(updatedSettings);
                setInitialSettings(updatedSettings);
                notifySuccess(t('all.successfully'));
            }
        } finally {
        }
    };

    const handleSaveSettings = async () => {
        setButtonLoad(true);
        try {
            const response = await dispatch(patchProjectNotifications(settings, singleProject.id));
            if (response.payload?.status === 200) {
                setInitialSettings(settings);
                dispatch(getProjectNotifications(singleProject.id));
                notifySuccess(t('all.successfully'));
            }
        } finally {
            setButtonLoad(false);
        }
    };

    if (projectNotificationsLoading) {
        return (
            <div className={'companies-page-loader'}>
                <LoadPanel noBg={true} circleSize={50} staticPos={true}/>
            </div>
        );
    }

    const hasChanges = JSON.stringify(settings) !== JSON.stringify(initialSettings);

    return (
        <div className={'projects__notification'}>
            <ProjectsHero
                showArrow={false}
                heading={t('profileNotification.projectNotification')}
                buttons={
                    <>
                        {allowActions && <IconButtonMUI onClick={handleNotificationRead} title={t('all.mute')}>
                            <MuteSvg/>
                        </IconButtonMUI>}
                    </>
                }
            />

            <div className='projects__notification-content'>
                <div className='projects__notification-list'>
                    {notification.map(({title, descriptions, key}, idx) => (
                        <div key={idx} className='switch'>
                            <SwitchMUI disabled={!allowActions} color='primary' checked={settings[key]} value={key}
                                       onChange={handleSwitchChange(key)}/>
                            <div className='switch__content'>
                                <div className='switch__title'>{title}</div>
                                <div className='switch__descriptions'>{descriptions}</div>
                            </div>
                        </div>
                    ))}
                </div>
                {allowActions &&
                    <ButtonMUI onClick={handleSaveSettings} loading={buttonLoad} disabled={!hasChanges || buttonLoad}>
                        {t('profileNotification.saveButton')}
                    </ButtonMUI>}

            </div>
        </div>
    );
};

export default ProjectsNotification;
